import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

/** Marks a navigation as this page's own write, so the search box never
 *  re-reads a value it wrote itself. */
const OWN_WRITE = { logisticsOwnWrite: true } as const;

const isOwnWrite = (state: unknown): boolean =>
  (state as Partial<typeof OWN_WRITE> | null)?.logisticsOwnWrite === true;

/**
 * The page's URL-backed view state: the search query (`queryKey`) and the
 * other keys (the open drawer, …), every write a `replace`.
 *
 * The query is held in LOCAL state and mirrored to the URL, not read back from
 * it. The router commits a navigation as a transition, so an input bound to
 * the URL lags its own keystrokes: a key typed before the previous one has
 * committed lands on the old value and the earlier character is lost. The
 * same lag let any other write built on the render-time params put an old
 * query back — so every write here carries the box's current text.
 *
 * A navigation this page did not make (a link, back/forward) is the one time
 * the URL wins: the box takes the new query.
 */
export function useLogisticsUrlState(queryKey: string) {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [query, setQueryState] = useState(() => searchParams.get(queryKey) ?? '');
  const [seenKey, setSeenKey] = useState(location.key);
  if (location.key !== seenKey) {
    setSeenKey(location.key);
    if (!isOwnWrite(location.state)) setQueryState(searchParams.get(queryKey) ?? '');
  }

  const write = (changes: Record<string, string | null>) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries({ [queryKey]: query, ...changes })) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true, state: OWN_WRITE },
    );

  return {
    searchParams,
    query,
    setQuery: (value: string) => {
      setQueryState(value);
      write({ [queryKey]: value });
    },
    setUrlParam: (key: string, value: string | null) => write({ [key]: value }),
  };
}
